import styled from '@emotion/styled';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const HHMMSS_PATTERN = /^\d{2}:\d{2}:\d{2}$/;

const extractChaptersFromSource = source => {
  if (!source) return null;
  if (Array.isArray(source)) return source;
  if (Array.isArray(source.chapters)) return source.chapters;
  if (Array.isArray(source.data?.chapters)) return source.data.chapters;
  return null;
};

const datetimeToRelativeSeconds = (datetime, baseTimestamp) => {
  if (!datetime || !baseTimestamp) return 0;
  const parsed = new Date(datetime).getTime();
  if (Number.isNaN(parsed)) return 0;
  return (parsed - baseTimestamp) / 1000;
};

const secondsToHHMMSS = totalSeconds => {
  const safeSeconds = Math.max(0, totalSeconds);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = Math.floor(safeSeconds % 60);

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
    2,
    '0'
  )}`;
};

const timeToSeconds = time => {
  if (!time) return 0;

  const parts = String(time)
    .split(':')
    .map(Number);

  if (parts.some(part => Number.isNaN(part))) return 0;

  if (parts.length === 3) {
    const [hours, minutes, seconds] = parts;
    return hours * 3600 + minutes * 60 + seconds;
  }

  if (parts.length === 2) {
    const [minutes, seconds] = parts;
    return minutes * 60 + seconds;
  }

  return parts[0] || 0;
};

const normalizeTopicSection = (section, baseTimestamp) => {
  const label = section.title || section.label || '';
  const startTimeRaw = section.start_time || section.start;
  const endTimeRaw = section.end_time || section.end;

  if (typeof startTimeRaw === 'string' && HHMMSS_PATTERN.test(startTimeRaw)) {
    return {
      label,
      start: startTimeRaw,
      end: endTimeRaw
    };
  }

  const startSeconds = datetimeToRelativeSeconds(startTimeRaw, baseTimestamp);
  const endSeconds = datetimeToRelativeSeconds(endTimeRaw, baseTimestamp);

  return {
    label,
    start: secondsToHHMMSS(startSeconds),
    end: secondsToHHMMSS(endSeconds)
  };
};

const getTranscriptBaseTimestamp = transcriptData => {
  try {
    const jsonData = transcriptData?.Response?.json_data;
    if (!jsonData) return 0;
    const parsed = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
    if (parsed?.[0]?.start) {
      const timestamp = new Date(parsed[0].start).getTime();
      return Number.isNaN(timestamp) ? 0 : timestamp;
    }
  } catch (e) {
    return 0;
  }
  return 0;
};

export const PlayerFrame = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1 1 auto;

  .player-chapter-ticks {
    opacity: ${props => (props.paused ? 1 : 0)};
    pointer-events: ${props => (props.paused ? 'auto' : 'none')};
  }

  &:hover .player-chapter-ticks {
    opacity: 1;
    pointer-events: auto;
  }
`;

const ChapterBarWrap = styled.div`
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 15px;
  z-index: 9;
  height: 14px;
  cursor: pointer;
  transition: opacity 0.15s ease;
`;

const ChapterTick = styled.button`
  position: absolute;
  top: 0;
  left: ${props => props.left};
  width: 12px;
  height: 100%;
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  transform: translateX(-50%);
  cursor: pointer;
  z-index: 2;

  &:before {
    content: '';
    position: absolute;
    top: 4px;
    left: 50%;
    width: 2px;
    height: 6px;
    background: #fff;
    transform: translateX(-50%);
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.35);
  }
`;

const HoverTooltip = styled.div`
  position: absolute;
  bottom: 16px;
  transform: translateX(-50%);
  background: rgba(16, 16, 16, 0.92);
  color: #fff;
  padding: 6px 8px;
  border-radius: 2px;
  pointer-events: none;
  min-width: 90px;
  max-width: 240px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);

  .chapter-title {
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.3;
    margin: 0 0 2px 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .chapter-time {
    color: #fff;
    font-size: 11px;
    font-weight: 400;
    margin: 0;
  }
`;

function PlayerChapterBar({ playerRef, duration, currentPlayedSecond, setPlaying, onChapterSelect }) {
  const [hoverState, setHoverState] = useState(null);
  const topicSectionsFromStore = useSelector(state => state.ProjectReducer?.topicSectionData);
  const chapterizationLoading = useSelector(state => state.ProjectReducer?.chapterizationLoading);
  const transcriptData = useSelector(state => state.ProjectReducer?.rtmTranscriptData);

  const topicSections = useMemo(() => {
    if (chapterizationLoading) return [];
    const rawChapters = extractChaptersFromSource(topicSectionsFromStore);
    if (!rawChapters?.length) return [];

    let baseTimestamp = getTranscriptBaseTimestamp(transcriptData);
    if (!baseTimestamp) {
      const firstStart = rawChapters[0]?.start_time || rawChapters[0]?.start;
      if (firstStart && !(typeof firstStart === 'string' && HHMMSS_PATTERN.test(firstStart))) {
        const parsed = new Date(firstStart).getTime();
        if (!Number.isNaN(parsed)) {
          baseTimestamp = parsed;
        }
      }
    }

    return rawChapters.map(section => normalizeTopicSection(section, baseTimestamp)).filter(section => section.label);
  }, [topicSectionsFromStore, transcriptData]);

  const videoDuration = duration || playerRef?.current?.getDuration() || 0;

  const chapterLayout = useMemo(() => {
    if (!topicSections.length) return [];

    const total = videoDuration || timeToSeconds(topicSections[topicSections.length - 1]?.end);
    if (!total) return [];

    return topicSections
      .map((section, index) => {
        const start = timeToSeconds(section.start);
        const nextStart = topicSections[index + 1] ? timeToSeconds(topicSections[index + 1].start) : total;
        const end = section.end ? timeToSeconds(section.end) : nextStart;
        const clampedStart = Math.max(0, Math.min(start, total));
        const clampedEnd = Math.max(clampedStart, Math.min(end, total));

        return {
          ...section,
          startSeconds: clampedStart,
          endSeconds: clampedEnd,
          left: `${(clampedStart / total) * 100}%`
        };
      })
      .filter(section => section.endSeconds > section.startSeconds);
  }, [topicSections, videoDuration]);

  if (!chapterLayout.length || !videoDuration) {
    return null;
  }

  const getSecondsFromEvent = event => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    return {
      seconds: ratio * videoDuration,
      x: event.clientX - rect.left,
      width: rect.width
    };
  };

  const getChapterForSeconds = seconds => {
    return (
      chapterLayout.find((section, index) => {
        const isLast = index === chapterLayout.length - 1;
        return seconds >= section.startSeconds && (seconds < section.endSeconds || (isLast && seconds <= section.endSeconds));
      }) || chapterLayout[chapterLayout.length - 1]
    );
  };

  const handleMove = event => {
    const { seconds, x, width } = getSecondsFromEvent(event);
    const chapter = getChapterForSeconds(seconds);
    setHoverState({
      seconds,
      x: Math.min(Math.max(x, 16), width - 16),
      chapter
    });
  };

  const handleLeave = () => {
    setHoverState(null);
  };

  const selectAtSeconds = seconds => {
    if (onChapterSelect) {
      onChapterSelect(seconds);
      return;
    }

    if (playerRef?.current) {
      playerRef.current.seekTo(seconds, 'seconds');
    }
    if (setPlaying) {
      setPlaying(true);
    }
  };

  const handleClick = event => {
    const { seconds } = getSecondsFromEvent(event);
    selectAtSeconds(seconds);
  };

  const handleTickClick = (event, section) => {
    event.stopPropagation();
    selectAtSeconds(section.startSeconds);
  };

  return (
    <ChapterBarWrap
      className="player-chapter-ticks"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onClick={handleClick}
    >
      {chapterLayout.map((section, index) => {
        if (index === 0 || section.startSeconds <= 0) return null;

        return (
          <ChapterTick
            key={`${section.label}-${index}`}
            type="button"
            left={section.left}
            onClick={event => handleTickClick(event, section)}
            aria-label={section.label}
          />
        );
      })}
      {hoverState && (
        <HoverTooltip style={{ left: `${hoverState.x}px` }}>
          <p className="chapter-title">{hoverState.chapter?.label}</p>
          <p className="chapter-time">{secondsToHHMMSS(hoverState.seconds)}</p>
        </HoverTooltip>
      )}
    </ChapterBarWrap>
  );
}

export default PlayerChapterBar;
